/**
 * MOTEUR DE FUITES DE LA GALAXIE SANTVIA -- une seule implementation, deux
 * produits (Klavox, Vigilvia) et un seul miroir souverain.
 *
 * Ce module est ecrit en **JavaScript ESM sans aucune dependance** (types
 * fournis a cote, `index.d.ts`) pour une raison precise : Klavox est une PWA
 * TypeScript compilee, Vigilvia sert des pages en JavaScript nu. Un module TS
 * n'aurait ete mutualisable que sur papier -- c'est exactement le « deux
 * implementations » que cette carte vient supprimer.
 *
 * Invariants (ils sont la raison d'etre du module, pas des details) :
 *   - k-anonymat : seuls les 5 premiers caracteres hexadecimaux du SHA-1
 *     quittent l'appareil. Jamais le mot de passe, jamais le hash entier.
 *     SHA-1 est impose par le protocole : c'est un identifiant de recherche,
 *     pas une protection.
 *   - echec franc : toute reponse illisible ou tout echec reseau fait echouer
 *     la verification. Un resultat partiel pris pour « aucune fuite » est le
 *     pire mensonge que ce moteur puisse produire.
 *   - source muette => `verifieLe: null` => niveau `non-verifie`. « Non
 *     verifie » n'est pas « aucune fuite » (contrat de socle `credentials.audit`).
 *
 * Point d'acces : NOTRE miroir UE (`tools/miroir-fuites.mjs`, meme protocole,
 * aucun journal), jamais l'API publique Pwned Passwords. Decision du porteur du
 * 2026-10-16 : aucun appel a un tiers hors UE, meme en k-anonymat -- ni en
 * defaut, ni en repli, ni par un reglage utilisateur. Les donnees viennent de
 * Have I Been Pwned (Troy Hunt, licence CC BY 4.0) ; la verification se fait
 * chez nous.
 */

/** Miroir souverain de la galaxie : le seul point d'acces par defaut, sans repli. */
export const SANTVIA_BREACH_ENDPOINT = 'https://fuites.santvia.eu';

/** Nom historique, conserve pour les consommateurs (Vigilvia) : vaut desormais le miroir souverain. */
export const PWNED_PASSWORDS_PUBLIC_ENDPOINT = SANTVIA_BREACH_ENDPOINT;

/** Services tiers hors UE refuses comme point d'acces (domaine ou sous-domaine). */
const REFUSED_BREACH_DOMAINS = ['pwnedpasswords.com', 'haveibeenpwned.com'];

const PREFIX_LENGTH = 5;
const RANGE_LINE = /^([0-9A-F]{35}):(\d+)$/iu;
const LOOPBACK = ['localhost', '127.0.0.1', '[::1]'];

export class BreachCheckError extends Error {
  constructor(message) {
    super(message);
    this.name = 'BreachCheckError';
  }
}

/**
 * Validation commune des points d'acces reseau saisis par l'utilisateur
 * (service de fuites, fournisseur d'alias e-mail, futur service souverain).
 * UN SEUL validateur pour toute la galaxie : HTTPS obligatoire (HTTP tolere en
 * boucle locale), ni identifiants ni parametres -- une URL qui en porte les
 * ferait fuiter dans les journaux d'acces de chaque intermediaire.
 */
export function normalizeHttpsEndpoint(raw, { label, fail }) {
  let url;
  try {
    url = new URL(String(raw).trim());
  } catch {
    throw fail(`URL ${label} invalide`);
  }
  const loopback = LOOPBACK.includes(url.hostname);
  if (url.protocol !== 'https:' && !(url.protocol === 'http:' && loopback)) {
    throw fail(`Le service ${label} doit etre en HTTPS`);
  }
  if (url.username !== '' || url.password !== '' || url.search !== '' || url.hash !== '') {
    throw fail(`L'URL ${label} ne doit contenir ni identifiants, ni parametres`);
  }
  return url.href.replace(/\/+$/u, '');
}

/** Le point d'acces designe-t-il un service tiers refuse (API publique Pwned Passwords) ? */
export function isRefusedBreachEndpoint(endpoint) {
  let host;
  try {
    host = new URL(String(endpoint).trim()).hostname.toLowerCase().replace(/\.$/u, '');
  } catch {
    return false;
  }
  return REFUSED_BREACH_DOMAINS.some((domain) => host === domain || host.endsWith(`.${domain}`));
}

/** Valide l'URL du service de fuites (notre miroir, ou un miroir auto-heberge) ; l'API publique est refusee. */
export function normalizeBreachEndpoint(raw) {
  const fail = (message) => new BreachCheckError(message);
  const endpoint = normalizeHttpsEndpoint(raw, { label: 'du service de fuites', fail });
  if (isRefusedBreachEndpoint(endpoint)) {
    throw fail(`Service tiers hors UE refuse : la verification des fuites passe par ${SANTVIA_BREACH_ENDPOINT}`);
  }
  return endpoint;
}

export async function sha1Hex(value) {
  const digest = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('').toUpperCase();
}

/** Stricte : une reponse inattendue (page HTML, portail captif) ne doit jamais passer pour « aucune fuite ». */
export function parseRangeResponse(body) {
  const counts = new Map();
  for (const line of body.split(/\r?\n/u)) {
    const trimmed = line.trim();
    if (trimmed === '') continue;
    const match = RANGE_LINE.exec(trimmed);
    if (match === null) throw new BreachCheckError('Reponse du service de fuites illisible');
    const count = Number(match[2]);
    if (count > 0) counts.set(match[1].toUpperCase(), count);
  }
  return counts;
}

/**
 * Renvoie, pour chaque mot de passe retrouve dans les fuites, son nombre
 * d'occurrences (absent = non trouve). Tout echec fait echouer l'ensemble :
 * pas de resultat partiel qui laisserait croire a un coffre sain. Le resultat
 * reste en memoire (cle = mot de passe en clair) et ne doit jamais etre persiste.
 */
export async function checkPwnedPasswords(passwords, { endpoint, fetchImpl = fetch, concurrency = 6 }) {
  const base = normalizeBreachEndpoint(endpoint);
  // prefixe -> (suffixe -> mot de passe) : une seule requete par prefixe partage.
  const byPrefix = new Map();
  for (const password of new Set(passwords)) {
    if (password === '') continue;
    const hash = await sha1Hex(password);
    const prefix = hash.slice(0, PREFIX_LENGTH);
    byPrefix.set(prefix, (byPrefix.get(prefix) ?? new Map()).set(hash.slice(PREFIX_LENGTH), password));
  }

  const found = new Map();
  const prefixes = [...byPrefix.keys()];
  let next = 0;
  const worker = async () => {
    while (next < prefixes.length) {
      const prefix = prefixes[next++];
      let response;
      try {
        response = await fetchImpl(`${base}/range/${prefix}`, {
          headers: { 'Add-Padding': 'true' },
          cache: 'no-store',
          credentials: 'omit',
          referrerPolicy: 'no-referrer',
        });
      } catch {
        throw new BreachCheckError('Service de fuites injoignable');
      }
      if (!response.ok) throw new BreachCheckError(`Service de fuites indisponible (HTTP ${response.status})`);
      const counts = parseRangeResponse(await response.text());
      for (const [suffix, password] of byPrefix.get(prefix)) {
        const count = counts.get(suffix);
        if (count !== undefined) found.set(password, count);
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, prefixes.length) }, worker));
  return found;
}

const PENALITE = { compromis: 60, faible: 25, reutilise: 15 };

/**
 * SCORE D'EXPOSITION POST-FUITE (mutualise : Vigilvia l'affiche par identite,
 * Klavox par coffre). Echelle inversee de la « Sante du coffre » : ici
 * **0 = aucune exposition connue, 100 = exposition maximale**.
 *
 * Regle non negociable : si UN SEUL verdict n'a pas ete verifie
 * (`verifieLe: null` -- source de fuites absente, en panne, consentement non
 * donne), le score vaut `null` et le niveau est `non-verifie`. Un identifiant
 * inconnu peut etre le pire du lot : le moyenner a zero afficherait un calme
 * que personne n'a mesure.
 */
export function computeExposureScore(verdicts) {
  const liste = [...verdicts];
  if (liste.length === 0 || liste.some((v) => !v.verifieLe)) {
    return { score: null, niveau: 'non-verifie', compromis: 0, total: liste.length };
  }
  const expositions = liste.map((v) =>
    Math.min(
      100,
      (v.compromis ? PENALITE.compromis : 0) + (v.faible ? PENALITE.faible : 0) + (v.reutilise ? PENALITE.reutilise : 0),
    ),
  );
  const score = Math.round(expositions.reduce((a, b) => a + b, 0) / expositions.length);
  const compromis = liste.filter((v) => v.compromis).length;
  // Le niveau suit le PIRE cas, pas la moyenne : un seul mot de passe fuite
  // reste critique meme noye dans un coffre sain.
  const niveau = compromis > 0 ? 'critique' : score >= 50 ? 'eleve' : score >= 20 ? 'moyen' : 'faible';
  return { score, niveau, compromis, total: liste.length };
}
